import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { BACKEND_URL } from '../../utils/config';
import { triggerSessionExpired } from '../../context/SessionContext';
import { logout } from '../slices/authSlice';

// Grace period: don't auto-logout within 5 s of storing a token
const LOGIN_GRACE_MS = 5000;

// Check if backend is v5 (currently local port 1338 is v5, or explicit env flag)
const isV5 = BACKEND_URL.includes('1338') || process.env.REACT_APP_IS_STRAPI_V5 === 'true';
const apiPrefix = isV5 ? '/api' : '';

// Create a custom base query with error handling
const baseQueryWithReauth = fetchBaseQuery({
  baseUrl: `${BACKEND_URL}${apiPrefix}`,
  credentials: 'include',
  prepareHeaders: (headers, { getState, endpoint, extraOptions }) => {
    // Identify public endpoints based on URL pattern
    if (isPublicEndpoint(endpoint)) {
      // For public endpoints, we don't need to add authentication headers
      return headers;
    }

    const reduxToken = getState()?.auth?.token;
    const lsToken = localStorage.getItem('token');
    const token = reduxToken || lsToken;

    if (process.env.NODE_ENV === 'development') {
      // console.log(`[Auth] [${endpoint}] token source: ${reduxToken ? 'redux' : lsToken ? 'localStorage' : 'NONE'}`);
    }

    // Add auth header if token exists
    if (token) {
      headers.set('Authorization', `Bearer ${token}`);
    }

    // Ensure proper content-type for JSON requests (if not FormData)
    if (!(extraOptions?.body instanceof FormData)) {
      headers.set('Content-Type', 'application/json');
    }

    return headers;
  }
});

const baseQueryWithErrorHandling = async (args, api, extraOptions) => {
  // For FormData, make sure we don't mess with the Content-Type
  // The browser will set it automatically with the correct boundary
  if (args.body instanceof FormData) {
    // Remove any existing Content-Type header to let the browser set it
    if (args.headers) {
      delete args.headers['Content-Type'];
    }
  }

  try {
    const result = await baseQueryWithReauth(args, api, extraOptions);

    if (result.error) {
      // Check if this is a public endpoint (based on URL pattern)
      const isPublicEndpointRequest = args.url && isPublicEndpoint(args.url);

      if (process.env.NODE_ENV === 'development') {
        console.warn(`[API] Error [${args.url}]: status=${result.error.status}`, result.error.data?.message || result.error.data || '');
      }

      // Handle authentication errors (except for public endpoints)
      // Only logout on 401 (invalid/expired token), NOT on 403 (permission denied).
      // In Strapi v3, 403 means the endpoint isn't allowed for the user's role,
      // which is different from the token being invalid.
      if (result.error.status === 401 && !isPublicEndpointRequest) {
        const wasLoggedIn = !!(localStorage.getItem('token') || localStorage.getItem('auth_token'));
        const authTimestamp = parseInt(localStorage.getItem('auth_timestamp') || '0', 10);
        const msSinceLogin = Date.now() - authTimestamp;

        if (process.env.NODE_ENV === 'development') {
          console.error(`[API] 401 on [${args.url}] — wasLoggedIn=${wasLoggedIn}, msSinceLogin=${msSinceLogin}. ${msSinceLogin < LOGIN_GRACE_MS ? 'SKIPPING logout (grace period)' : 'Dispatching logout!'}`);
        }

        // Skip logout if we just stored a fresh token (avoids race-condition logout)
        if (wasLoggedIn && msSinceLogin >= LOGIN_GRACE_MS) {
          api.dispatch(logout());
          triggerSessionExpired();
        }
      }
    }

    return result;
  } catch (error) {
    console.error('API request failed:', error);
    return { error: { status: 'FETCH_ERROR', error: error.message } };
  }
};

export const createBaseApi = (reducerPath) => {
  return createApi({
    reducerPath,
    baseQuery: baseQueryWithErrorHandling,
    tagTypes: [
      'Auth',
      'User',
      'ClosetClothes',
      'Closets',
      'Lookbooks',
      'LookbookEntries',
      'PersonImage',
      'BackgroundImage',
      'VTOResult',
      'Upload',
      'Credit',
      'SavedPaymentMethods',
      'SharedCloset',
      'Earnings',
      'Referral',
      'ShareBundle'
    ],
    endpoints: () => ({}),
    keepUnusedDataFor: 5 * 60, // Keep unused data in cache for 5 minutes
    refetchOnMountOrArgChange: 30, // Refetch after 30 seconds
    refetchOnFocus: false,
    refetchOnReconnect: true
  });
};

export const createProtectedEndpoint = (builder, endpoint) => {
  return builder.query({
    ...endpoint,
    transformErrorResponse: (response) => {
      if (response.status === 401) {
        // Handle unauthorized access
        return { error: 'Unauthorized access' };
      }
      return response;
    }
  });
};

// List of endpoints that don't require authentication
const isPublicEndpoint = (endpoint) => {
  return (
    endpoint.includes('login') ||
    endpoint.includes('register') ||
    endpoint.includes('forgot-password') ||
    endpoint.includes('reset-password') ||
    endpoint.includes('verify-email') ||
    endpoint.includes('shared-results/by-token') ||
    endpoint.includes('shared-closet/profile')
  );
};